import React, { useState, useMemo } from 'react';
import { Card, Button, Checkbox, Pagination, Space, Empty, Drawer, Spin } from 'antd';
import { ReloadOutlined, UnorderedListOutlined, DownOutlined, UpOutlined, ExpandOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getGridTotal, useDataGrid } from './DataGridContext';
import { useModal } from '../../context/ModalContext';
import ColumnFilter from '../ColumnFilter';
import './MobileCardList.css';

export default function MobileCardList({ name, title, mobileCardTitle, rowClassName }) {
    const { t } = useTranslation();
    const {
        resultModel,
        dataGridProps,
        setSelectedRows,
        onFilterPageSort,
        selectedRows,
        queryModel,
        refreshDataGrid,
        count,
        loadExactCount
    } = useDataGrid();
    const { openModal, closeModal } = useModal();
    const [expandedCards, setExpandedCards] = useState({});
    const [drawerRecord, setDrawerRecord] = useState(null);

    const data = resultModel?.data || [];
    const pagination = resultModel?.pagination || {};
    const rowKey = dataGridProps.rowKey || 'id';

    const {total: gridTotal, deferred: deferredCount, capped, known} = getGridTotal(resultModel, count);
    // Sayım henüz gelmedi: tam sayfa spinner yerine burada, toplam alanında küçük bir yerel spinner göster.
    const totalLabel = !deferredCount
        ? gridTotal
        : (!known
            ? <Spin indicator={<LoadingOutlined spin/>} size="small" style={{margin: '0 4px'}}/>
            : (capped ? `${count.value}+` : count.value));

    const visibleColumns = useMemo(() => {
        const allColumns = dataGridProps.columns || [];
        const selectedCols = JSON.parse(localStorage.getItem(`data-grid-${name}`) || '[]');
        if (selectedCols.length === 0) return allColumns;
        return allColumns.filter(x => selectedCols.find(y => y.key === x.key));
    }, [dataGridProps.columns, name]);

    const { titleColumn, primaryColumns, secondaryColumns, actionColumn, bodyColumns, allBodyColumns } = useMemo(() => {
        const nonActionColumns = visibleColumns.filter(col =>
            col.key !== 'actions' && col.key !== 'action' && col.dataIndex !== 'actions'
        );
        const action = visibleColumns.find(col =>
            col.key === 'actions' || col.key === 'action' || col.dataIndex === 'actions'
        );

        // A function mobileCardTitle renders the whole card itself, so no column
        // may go to the collapse area - it would just repeat the card content.
        if (typeof mobileCardTitle === 'function') {
            return {
                titleColumn: null,
                primaryColumns: [],
                secondaryColumns: [],
                actionColumn: action,
                bodyColumns: [],
                allBodyColumns: []
            };
        }

        // Find title column by mobileCardTitle (can be key or dataIndex)
        const titleCol = mobileCardTitle
            ? nonActionColumns.find(col => col.key === mobileCardTitle || col.dataIndex === mobileCardTitle)
            : nonActionColumns[0];

        // Remove title column from others - all go to collapse
        const otherColumns = nonActionColumns.filter(col => col !== titleCol);

        return {
            titleColumn: titleCol,
            primaryColumns: [titleCol].filter(Boolean),
            secondaryColumns: [],
            actionColumn: action,
            bodyColumns: [],
            allBodyColumns: otherColumns // All columns except title go to collapse
        };
    }, [visibleColumns, mobileCardTitle]);

    const toggleExpand = (recordId) => {
        setExpandedCards(prev => ({ ...prev, [recordId]: !prev[recordId] }));
    };

    const isSelected = (record) => selectedRows.some(row => row[rowKey] === record[rowKey]);

    const handleSelect = (record, checked) => {
        if (checked) {
            setSelectedRows([...selectedRows, record]);
        } else {
            setSelectedRows(selectedRows.filter(row => row[rowKey] !== record[rowKey]));
        }
    };

    const handleSelectAll = (checked) => {
        setSelectedRows(checked ? [...data] : []);
    };

    const handlePageChange = (page, pageSize) => {
        onFilterPageSort({
            filter: queryModel.filter,
            pagination: { pageNumber: page, pageSize },
            orderByItems: queryModel.orderByItems
        });
    };

    const renderCellValue = (column, record) => {
        if (column?.render) {
            return column.render(record[column.dataIndex], record, 0);
        }
        const value = column?.dataIndex ? record[column.dataIndex] : null;
        if (value === null || value === undefined) return '-';
        if (typeof value === 'boolean') return value ? t('Yes') : t('No');
        return value;
    };

    const getRowClassName = (record, index) => {
        if (typeof rowClassName === 'function') {
            return rowClassName(record, index);
        }
        return rowClassName || '';
    };

    const renderCard = (record, index) => {
        const recordId = record[rowKey];
        const isExpanded = expandedCards[recordId];
        const canOpenDetails = !!dataGridProps.rowExpandable?.(record);
        // A function mobileCardTitle gets openDetails and places the button itself;
        // only the plain column-based card falls back to a button in the expand area.
        const showDetailsInExpand = canOpenDetails && typeof mobileCardTitle !== 'function';
        const hasExpandableContent = allBodyColumns.length > 0 || !!actionColumn || showDetailsInExpand;
        const customRowClass = getRowClassName(record, index);

        return (
            <Card
                key={recordId}
                className={`mobile-data-card ${isSelected(record) ? 'selected' : ''} ${customRowClass}`}
                size="small"
                onClick={() => hasExpandableContent && toggleExpand(recordId)}
            >
                <div className="mobile-card-header">
                    {dataGridProps.enableRowSelection && (
                        <Checkbox
                            checked={isSelected(record)}
                            onChange={(e) => handleSelect(record, e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                        />
                    )}
                    <div className="mobile-card-title">
                        {typeof mobileCardTitle === 'function' ? (
                            mobileCardTitle(record, {
                                canOpenDetails,
                                openDetails: () => setDrawerRecord(record)
                            })
                        ) : titleColumn ? (
                            <>
                                <span className="mobile-card-title-label">{titleColumn.title}</span>
                                <span className="mobile-card-title-value">{renderCellValue(titleColumn, record)}</span>
                            </>
                        ) : (
                            <>
                                <span className="mobile-card-title-label">ID</span>
                                <span className="mobile-card-title-value">{record[rowKey]}</span>
                            </>
                        )}
                    </div>
                    {hasExpandableContent && (
                        <Button 
                            type="text" 
                            size="small" 
                            icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(recordId);
                            }}
                        />
                    )}
                </div>

                {hasExpandableContent && isExpanded && (
                    <div className="mobile-card-expanded">
                        {allBodyColumns.map((column) => (
                            <div key={column.key || column.dataIndex} className="mobile-card-field">
                                <span className="mobile-card-field-label">{column.title}</span>
                                <span className="mobile-card-field-value">{renderCellValue(column, record)}</span>
                            </div>
                        ))}
                        {actionColumn && (
                            <div className="mobile-card-field mobile-card-actions-row" onClick={(e) => e.stopPropagation()}>
                                <span className="mobile-card-field-label">{actionColumn.title || t('Actions')}</span>
                                <span className="mobile-card-field-value">{renderCellValue(actionColumn, record)}</span>
                            </div>
                        )}
                        {showDetailsInExpand && (
                            <div className="mobile-card-expand-row" onClick={(e) => e.stopPropagation()}>
                                <Button 
                                    type="primary" 
                                    icon={<ExpandOutlined />}
                                    onClick={() => setDrawerRecord(record)}
                                    block
                                >
                                    {t('Details')}
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </Card>
        );
    };

    return (
        <div className="mobile-card-list-container">
            <div className="mobile-card-list-toolbar">
                <Space>
                    {dataGridProps.enableRowSelection && (
                        <Checkbox
                            checked={selectedRows.length === data.length && data.length > 0}
                            indeterminate={selectedRows.length > 0 && selectedRows.length < data.length}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                        >
                            {selectedRows.length > 0 && `(${selectedRows.length})`}
                        </Checkbox>
                    )}
                </Space>
                <Space>
                    <Button
                        type="text"
                        size="small"
                        icon={<UnorderedListOutlined />}
                        onClick={() => {
                            openModal(t('Hide/Show Columns'), (
                                <ColumnFilter
                                    columns={dataGridProps.columns}
                                    selectedColumnKeys={visibleColumns.map(x => x.key)}
                                    onSelectedColumns={(cols) => {
                                        localStorage.setItem(`data-grid-${name}`, JSON.stringify(cols));
                                        closeModal();
                                    }}
                                />
                            ));
                        }}
                    />
                    <Button size="small" icon={<ReloadOutlined />} onClick={() => refreshDataGrid()} />
                </Space>
            </div>

            {/* Grid tools get their own full-width row: inside the toolbar Space they
                cannot wrap and push the page into a horizontal scroll. */}
            {title && (
                <div className="mobile-card-list-tools">
                    {title()}
                </div>
            )}

            {data.length === 0 ? (
                <Empty description={t('No Data')} />
            ) : (
                <div className="mobile-card-list">
                    {data.map((record, index) => renderCard(record, index))}
                </div>
            )}

            {gridTotal > 0 && (
                <div className="mobile-card-list-pagination">
                    <Pagination
                        current={pagination.pageNumber || 1}
                        pageSize={pagination.pageSize || 10}
                        total={gridTotal}
                        onChange={handlePageChange}
                        size="small"
                        simple
                    />
                    <span className="mobile-pagination-info">
                        {/* Sayım ayrı istekte gelen grid'lerde: gelmediyse "…", tavanda kesildiyse "500+". */}
                        {t('Total')} {totalLabel} {t('records')}
                        {deferredCount && capped && (
                            <Button className="btn-accent" size="small" loading={count.loading} onClick={loadExactCount}>
                                {t('getExactCount')}
                            </Button>
                        )}
                    </span>
                </div>
            )}

            {/* Expandable Row Drawer */}
            <Drawer
                title={drawerRecord ? (
                    typeof mobileCardTitle === 'function'
                        ? mobileCardTitle(drawerRecord, { canOpenDetails: false, openDetails: () => {} })
                        : drawerRecord[rowKey]
                ) : null}
                placement="bottom"
                height="90%"
                onClose={() => setDrawerRecord(null)}
                open={!!drawerRecord}
                styles={{ body: { padding: 0 } }}
            >
                {drawerRecord && dataGridProps.expandedRowRender && (
                    dataGridProps.expandedRowRender(drawerRecord, 0, 0, true)
                )}
            </Drawer>
        </div>
    );
}